import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import useAppSelector from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';
import partitionHistories from './history/partitionHistories';
import isVerifier from './isVerifier';

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const USE_POST_VERIFY_NAVIGATION_QUERY = gql(/** @complexity 23 */ `
    query usePostVerifyNavigationQuery($postId: ID!) {
      post(id: $postId) {
        id
        content(isLatest: true) {
          id
          version
        }
        histories {
          id
          versionFrom
          ...partitionHistories_history
        }
        verificationEntries {
          id
          version
        }
        ...postPath_post
        ...isVerifier_post
      }
    }
  `);

const usePostVerifyNavigation = (postId: string) => {
  const [loadPostData, { loading }] = useLazyQuery(
    USE_POST_VERIFY_NAVIGATION_QUERY,
    {
      fetchPolicy: 'network-only',
      variables: {
        postId,
      },
    },
  );
  const currentUser = useAppSelector((state) => state.session.user);
  const navigate = useNavigate();

  const postVerifyNavigate = useCallback(
    async ({ replace }: { replace?: boolean } = {}) => {
      const { data, error } = await loadPostData();
      const post = data?.post;

      // If there's an error, i.e. when the post is not found,
      // we redirect to the post and let it handle the error.
      if (error) {
        navigate(postPath({ id: postId, title: '' }), {
          replace,
        });
        return;
      }

      if (post == null) return;

      // verificationEntires are in reverse chronological order
      const lastVerificationEntry = post.verificationEntries.at(0);

      const { accessibleHistories } = partitionHistories(post.histories);
      const firstAccessibleHistory = accessibleHistories.at(-1);

      if (
        lastVerificationEntry &&
        isVerifier(post, currentUser) &&
        // We need to make sure that the version we are trying to verify is actually
        // within the accessible history range, otherwise we'll end up in an infinite redirect loop.
        // If the version is not accessible, we fall back to the basic reverify modal without showing the diff.
        firstAccessibleHistory &&
        firstAccessibleHistory.versionFrom <= lastVerificationEntry.version
      ) {
        // We get some false positives like this, i.e. if the version changed due to a format change
        // The upside is that we also catch cases where  a comment was added or resolved.
        // We handle the false positives directly in PostVerifyView, where we load the changes anyways.
        if (post.content.version > lastVerificationEntry.version) {
          navigate(
            `${postPath(post)}/verify?histories=${lastVerificationEntry.version}-${post.content.version}`,
            {
              replace,
            },
          );
          return;
        }
      }

      navigate(postPath(post), {
        state: { verification: true },
        replace,
      });
    },
    [loadPostData, navigate, currentUser, postId],
  );

  return { postVerifyNavigate, loading };
};

export default usePostVerifyNavigation;
